import { CLASS_GROUPS } from "./classOptions"
import { CORE_SKILL_LEVELS, type CoreSkillLevel } from "./skillLevelConstants"

// Fee type identifiers
export enum FEE_TYPES {
  TUITION = "TUITION",
  SUPPLIES = "SUPPLIES",
  MATERIALS = "MATERIALS",
}

// Discount type identifiers
export enum DISCOUNT_TYPES {
  SIBLING = "SIBLING",
  EXTRA_CLASS = "EXTRA_CLASS",
}

// Session threshold for semester vs monthly rate
// Semester rate applies when:
// 1. Signing up for 5+ sessions, OR
// 2. Fewer than 5 sessions remain in semester AND signing up for all remaining sessions
export const SESSION_THRESHOLD_FOR_SEMESTER_RATE = 5

/**
 * Determines if semester rate should apply
 * @param sessionsSigningUpFor - Number of sessions the student is enrolling in
 * @param totalSessionsRemaining - Total sessions remaining in the semester
 * @returns true if semester rate applies
 */
export const shouldUseSemesterRate = (
  sessionsSigningUpFor: number,
  totalSessionsRemaining: number
): boolean => {
  // Standard case: 5+ sessions
  if (sessionsSigningUpFor >= SESSION_THRESHOLD_FOR_SEMESTER_RATE) {
    return true
  }
  // Edge case: fewer than 5 sessions remain, but signing up for all of them
  if (
    totalSessionsRemaining < SESSION_THRESHOLD_FOR_SEMESTER_RATE &&
    sessionsSigningUpFor >= totalSessionsRemaining
  ) {
    return true
  }
  return false
}

// Tuition rates per session
export const TUITION_RATES = {
  standard: {
    monthly: 78, // < 5 sessions
    semester: 67, // 5+ sessions
  },
  portfolio: {
    // monthly: 165, // No monthly pricing for portfolio
    semester: 132, // 5+ sessions
  },
} as const

// Sculpture materials fee per session
export const SCULPTURE_MATERIALS_FEE_PER_SESSION = 3

// Tryout tuition rates (single session)
export const TRYOUT_TUITION_RATES = {
  standard: 59,
  portfolio: 99,
} as const

// Supplies fee definitions
export const SUPPLIES_FEES = {
  coreArtBasicSupplies: {
    id: "coreArtBasicSupplies",
    label: "Core Art Basic Supplies (Bubblegum/Purple/Blueberry/Blue)",
    price: 39,
  },
  coreArtGreenLimeSupplies: {
    id: "coreArtGreenLimeSupplies",
    label: "Core Art Green/Lime Supplies",
    price: 123,
  },
  coreArtYellowOrangeRedSupplies: {
    id: "coreArtYellowOrangeRedSupplies",
    label: "Core Art Yellow/Orange/Red Supplies",
    price: 147,
  },
  sketchingSupplies: {
    id: "sketchingSupplies",
    label: "Sketching Supplies",
    price: 33,
  },
  collegePortfolioSupplies: {
    id: "collegePortfolioSupplies",
    label: "College Portfolio & AP Studio Art Supplies",
    price: 147,
  },
} as const

export type SuppliesFeeId = keyof typeof SUPPLIES_FEES

// Core skill levels that map to basic supplies (BG, P, BB, B and their ^ variants)
export const BASIC_SKILL_LEVELS: CoreSkillLevel[] = [
  CORE_SKILL_LEVELS.BG,
  CORE_SKILL_LEVELS["BG^"],
  CORE_SKILL_LEVELS.P,
  CORE_SKILL_LEVELS["P^"],
  CORE_SKILL_LEVELS.BB,
  CORE_SKILL_LEVELS["BB^"],
  CORE_SKILL_LEVELS.B,
  CORE_SKILL_LEVELS["B^"],
]

// Core skill levels that map to green/lime supplies (G, L and their ^ variants)
export const GREEN_LIME_SKILL_LEVELS: CoreSkillLevel[] = [
  CORE_SKILL_LEVELS.G,
  CORE_SKILL_LEVELS["G^"],
  CORE_SKILL_LEVELS.L,
  CORE_SKILL_LEVELS["L^"],
]

// Core skill levels that map to yellow/orange/red supplies (Y, O, R and their ^ variants)
export const YELLOW_ORANGE_RED_SKILL_LEVELS: CoreSkillLevel[] = [
  CORE_SKILL_LEVELS.Y,
  CORE_SKILL_LEVELS["Y^"],
  CORE_SKILL_LEVELS.O,
  CORE_SKILL_LEVELS["O^"],
  CORE_SKILL_LEVELS.R,
]

/**
 * Maps a core skill level to the appropriate supplies fee ID
 */
export const getSuppliesFeeIdForCoreSkillLevel = (
  skillLevel: CoreSkillLevel
): SuppliesFeeId | null => {
  if (BASIC_SKILL_LEVELS.includes(skillLevel)) {
    return "coreArtBasicSupplies"
  }
  if (GREEN_LIME_SKILL_LEVELS.includes(skillLevel)) {
    return "coreArtGreenLimeSupplies"
  }
  if (YELLOW_ORANGE_RED_SKILL_LEVELS.includes(skillLevel)) {
    return "coreArtYellowOrangeRedSupplies"
  }
  return null
}

/**
 * Class groups that use portfolio tuition rates
 */
export const PORTFOLIO_TUITION_CLASS_GROUPS = [CLASS_GROUPS.PORTFOLIO] as const

/**
 * Class groups that have supplies fees
 */
export const CLASS_GROUPS_WITH_SUPPLIES = [
  CLASS_GROUPS.CORE,
  CLASS_GROUPS.PAINTING,
  CLASS_GROUPS.PORTFOLIO,
] as const

/**
 * Class groups that have per-session materials fees
 * Sculpture materials fee removed — tuition-only pricing for now
 */
export const CLASS_GROUPS_WITH_MATERIALS = [] as const
// export const CLASS_GROUPS_WITH_MATERIALS = [CLASS_GROUPS.SCULPTURE] as const

/**
 * Checks if a class group uses portfolio tuition rates
 */
export const isPortfolioTuition = (classGroup: CLASS_GROUPS): boolean => {
  return (PORTFOLIO_TUITION_CLASS_GROUPS as readonly CLASS_GROUPS[]).includes(
    classGroup
  )
}

/**
 * Checks if a class group has supplies fees
 */
export const hasSuppliesFee = (classGroup: CLASS_GROUPS): boolean => {
  return (CLASS_GROUPS_WITH_SUPPLIES as readonly CLASS_GROUPS[]).includes(
    classGroup
  )
}

/**
 * Checks if a class group has per-session materials fees
 */
export const hasMaterialsFee = (classGroup: CLASS_GROUPS): boolean => {
  return (CLASS_GROUPS_WITH_MATERIALS as readonly CLASS_GROUPS[]).includes(
    classGroup
  )
}

// Fee definition type for UI rendering
export type FeeDefinition = {
  id: string
  label: string
  price: number
  feeType: FEE_TYPES
}

// All supplies fees as array for UI
export const SUPPLIES_FEES_ARRAY: FeeDefinition[] = Object.values(
  SUPPLIES_FEES
).map((fee) => ({
  ...fee,
  feeType: FEE_TYPES.SUPPLIES,
}))

// Get supplies fee by ID
export const getSuppliesFeeById = (
  id: SuppliesFeeId
): (typeof SUPPLIES_FEES)[SuppliesFeeId] => {
  return SUPPLIES_FEES[id]
}
